'use client';

import classNames from 'classnames';
import { validate } from 'email-validator';
import { type ChangeEvent, type FormEvent, useState } from 'react';

import { PayloadSlateRenderer } from '@/components/PayloadSlateRenderer/PayloadSlateRenderer';
import type { DataAttributes } from '@/types';
import { Button } from '@/ui/Button/Button';

import type { Cta } from '../Cta';
import { useFormSubmit } from '../lib/useFormSubmit';
import { FormStatus } from '../types';

import styles from './SubscribeForm.module.scss';

interface Props extends DataAttributes {
    id: Cta.Props['id'];
    classes?: {
        form?: string;
        input?: string;
        button?: string;
    };
    title: Cta.Props['title'];
    buttonLabel: Cta.Props['buttonLabel'];
    successMessage: Cta.Props['successMessage'];
    isHeroForm?: boolean;
}

export function SubscribeForm({
    classes,
    id,
    isHeroForm,
    title,
    buttonLabel,
    successMessage,
}: Props) {
    const [email, setEmail] = useState('');
    const [emailValidationError, setEmailValidationError] = useState<null | string>(null);
    const { status, error, handleSubmit: onSubmit } = useFormSubmit(title, id);
    const btnLabel = buttonLabel ? buttonLabel.trim() : undefined;
    const hasSubscribed = status === FormStatus.SUBMITTED;

    function handleSubmit(event: FormEvent) {
        event.preventDefault();

        if (!validate(email)) {
            setEmailValidationError('Please provide a valid email address');
            return;
        }

        onSubmit({ ctaId: id, email });
    }

    function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
        setEmailValidationError(null);
        setEmail(event.target.value);
    }

    return (
        <>
            {hasSubscribed && (
                <div className={classNames(styles.success)} data-subscribe-form-success-message>
                    {successMessage && successMessage.length ? (
                        <PayloadSlateRenderer nodes={successMessage} />
                    ) : (
                        <span>Thank you for subscribing!</span>
                    )}
                </div>
            )}

            {!hasSubscribed && (
                <form
                    className={classNames(styles.formWrapper, {
                        [styles.hero]: isHeroForm,
                        [styles.withError]: Boolean(emailValidationError),
                    })}
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <div className={classNames(styles.form, classes?.form)}>
                        <input
                            className={classNames(styles.input, classes?.input)}
                            placeholder="Enter your e-mail address"
                            type="email"
                            pattern=".*"
                            value={email}
                            onChange={handleEmailChange}
                            aria-label="Email"
                        />
                        <Button
                            className={classNames(styles.button, classes?.button)}
                            disabled={status === FormStatus.SUBMITTING}
                            variant="primary"
                            type="submit"
                        >
                            {btnLabel ?? 'Subscribe'}
                        </Button>
                    </div>

                    {emailValidationError && (
                        <div className={styles.error} data-subscribe-form-error-message>
                            {emailValidationError}
                        </div>
                    )}

                    {status === FormStatus.ERROR && error && (
                        <div className={styles.error} data-subscribe-form-error-message>
                            {error}
                        </div>
                    )}
                </form>
            )}
        </>
    );
}
